import { useEffect, useState } from 'react';

import { CreditCalculationModel } from '../../../../../../../backend/src/billing/billing-types';
import { resolveBillingPlan } from '../../../../../../../backend/src/billing/resolve-billing-plan';
import type { BillingPlanHistoryResponseDto } from '../../../../../../../backend/src/organisation/dto/billing-plan-history.response.dto';
import { PricingModel } from '../../../../../../../backend/src/types/types';
import { useUpdateCurrentBillingPeriod } from '@/api/mutations/useUpdateOrganisationBillingPeriod';
import { useGetOrganisationBillingPlanHistory } from '@/api/queries/useGetOrganisationBillingPlanHistory';
import {
  SettingsRow,
  SettingsSaveButton,
  SettingsSaveStatus,
  SettingsSection,
  SettingsSectionFooter,
} from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { formatDatetimeForTable } from '@/new/lib/table-utils';
import { NewBillingPeriodDialog } from '@/new/pages/admin/organisations/_organisationId/new-billing-period-dialog';

export function CurrentBillingPeriod({
  organisationId,
  plan,
}: {
  organisationId: string;
  plan: BillingPlanHistoryResponseDto;
}) {
  const { mutate, isLoading } = useUpdateCurrentBillingPeriod(organisationId);
  const [maxCredits, setMaxCredits] = useState('');
  const [pages, setPages] = useState('');
  const [seats, setSeats] = useState('');
  const [defaultLimit, setDefaultLimit] = useState('');
  useEffect(() => {
    setMaxCredits(String(plan.maxCredits ?? ''));
    setPages(String(plan.pagesPerCredit));
    setSeats(String(plan.seatCount ?? ''));
    setDefaultLimit(String(plan.defaultCreditLimit ?? ''));
  }, [plan]);
  const seatBased = plan.pricingModel === PricingModel.SeatBased;
  const changed =
    maxCredits !== String(plan.maxCredits ?? '') ||
    pages !== String(plan.pagesPerCredit) ||
    seats !== String(plan.seatCount ?? '') ||
    defaultLimit !== String(plan.defaultCreditLimit ?? '');
  const validNumber = (value: string, min: number) =>
    value.trim() !== '' && Number.isInteger(Number(value)) && Number(value) >= min && Number(value) <= 2147483647;
  const valid =
    validNumber(pages, 1) &&
    (seatBased ? validNumber(seats, 1) && validNumber(defaultLimit, 1) : validNumber(maxCredits, 0));
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!changed || !valid) return;
        mutate({
          planId: plan.id,
          pagesPerCredit: Number(pages),
          ...(seatBased
            ? { seatCount: Number(seats), defaultCreditLimit: Number(defaultLimit) }
            : { maxCredits: Number(maxCredits) }),
        });
      }}
    >
      <SettingsSection
        title="Current billing period"
        description="Update this period’s limits and page conversion rate. Changes take effect immediately and are recorded in plan history."
        footer={
          <SettingsSectionFooter>
            <SettingsSaveStatus variant={changed ? 'unsaved' : 'saved'}>
              {changed ? 'You have unsaved changes' : 'All changes saved'}
            </SettingsSaveStatus>
            <SettingsSaveButton
              type="submit"
              hasUnsavedChanges={changed}
              isLoading={isLoading}
              disabled={!changed || !valid}
            />
          </SettingsSectionFooter>
        }
      >
        <SettingsRow title="Billing period start">
          <span className="text-sm">
            {plan.billingStartDate ? formatDatetimeForTable(plan.billingStartDate) : 'Not set'}
          </span>
        </SettingsRow>
        <SettingsRow title="Billing period end">
          <span className="text-sm">
            {plan.billingEndDate ? formatDatetimeForTable(plan.billingEndDate) : 'Not set'}
          </span>
        </SettingsRow>
        <SettingsRow
          title="Pricing model"
          description="Fixed for this billing period. Start a new period to change it."
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block" tabIndex={0}>
                <Input
                  aria-label="Current pricing model"
                  className="w-48"
                  value={seatBased ? 'Seat-based' : 'Usage-based'}
                  disabled
                  readOnly
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>Create a new billing period to change the pricing model.</TooltipContent>
          </Tooltip>
        </SettingsRow>
        <SettingsRow
          title="Credit calculation model"
          description="Fixed for this billing period. Start a new period to change it."
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block" tabIndex={0}>
                <Input
                  aria-label="Current credit calculation model"
                  className="w-48"
                  value={plan.creditCalculationModel === CreditCalculationModel.Legacy ? 'Legacy' : 'Cost-based'}
                  disabled
                  readOnly
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>Create a new billing period to change the credit calculation model.</TooltipContent>
          </Tooltip>
        </SettingsRow>
        <SettingsRow
          title="Pages per credit"
          description={
            plan.creditCalculationModel === CreditCalculationModel.Legacy
              ? 'Legacy plans use one page per credit.'
              : undefined
          }
        >
          <Input
            aria-label="Current pages per credit"
            className="w-48"
            type="number"
            min={1}
            max={2147483647}
            step={1}
            value={pages}
            onChange={(event) => setPages(event.target.value)}
            disabled={isLoading || plan.creditCalculationModel === CreditCalculationModel.Legacy}
          />
        </SettingsRow>
        {seatBased ? (
          <>
            <SettingsRow title="Number of seats">
              <Input
                aria-label="Current number of seats"
                className="w-48"
                type="number"
                min={1}
                max={2147483647}
                step={1}
                value={seats}
                onChange={(event) => setSeats(event.target.value)}
                disabled={isLoading}
              />
            </SettingsRow>
            <SettingsRow
              title="Default credit limit"
              description="Changing this resets every active user’s individual credit limit and sets the default for new users."
            >
              <Input
                aria-label="Current default credit limit"
                className="w-48"
                type="number"
                min={1}
                max={2147483647}
                step={1}
                value={defaultLimit}
                onChange={(event) => setDefaultLimit(event.target.value)}
                disabled={isLoading}
              />
            </SettingsRow>
            <SettingsRow title="Hard limit multiplier">
              <span className="text-sm">{plan.shadowLimitCoefficient} × the user’s credit limit</span>
            </SettingsRow>
          </>
        ) : (
          <SettingsRow title="Maximum credits">
            <Input
              aria-label="Current maximum credits"
              className="w-48"
              type="number"
              min={0}
              max={2147483647}
              step={1}
              value={maxCredits}
              onChange={(event) => setMaxCredits(event.target.value)}
              disabled={isLoading}
            />
          </SettingsRow>
        )}
      </SettingsSection>
    </form>
  );
}

export function BillingPeriodSettings({ organisationId }: { organisationId: string }) {
  const { data: plans, isLoading, isError, refetch } = useGetOrganisationBillingPlanHistory(organisationId);
  const [creating, setCreating] = useState(false);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(interval);
  }, []);
  if (isLoading)
    return (
      <SettingsSection title="Current billing period">
        <div className="p-5">
          <Skeleton className="h-40 w-full" />
        </div>
      </SettingsSection>
    );
  if (isError || !plans)
    return (
      <SettingsSection title="Current billing period">
        <div role="alert" className="space-y-3 p-5 text-sm">
          <p>Billing plans could not be loaded.</p>
          <Button onClick={() => refetch()}>Try again</Button>
        </div>
      </SettingsSection>
    );
  const { plan, source } = resolveBillingPlan(plans, now);
  const current = source === 'current' && plan?.billingStartDate && plan.billingEndDate ? plan : null;
  return (
    <>
      {current ? (
        <CurrentBillingPeriod organisationId={organisationId} plan={current} />
      ) : (
        <SettingsSection title="Current billing period">
          <div className="space-y-2 p-6 text-sm">
            <p className="font-medium text-gray-800">No current billing period</p>
            <p className="text-gray-500">
              {source === 'legacy'
                ? 'No billing plans have been recorded. Usage falls back to legacy values: one page per credit and 10 credits per Kim thread.'
                : `Usage falls back to the ${source === 'future' ? 'next' : 'previous'} billing plan${plan?.billingStartDate ? ` starting ${formatDatetimeForTable(plan.billingStartDate)}` : ''}.`}
            </p>
            {plan && (
              <p className="text-gray-500">
                {plan.creditCalculationModel === CreditCalculationModel.Legacy ? 'Legacy' : 'Cost-based'} ·{' '}
                {plan.pagesPerCredit} {plan.pagesPerCredit === 1 ? 'page' : 'pages'} per credit.
              </p>
            )}
          </div>
        </SettingsSection>
      )}
      <SettingsSection id="new-billing-period" className="scroll-mt-8">
        <SettingsRow
          title="Create a new billing period"
          description="Set the dates, pricing model and initial limits for your next billing period."
        >
          <Button onClick={() => setCreating(true)}>Create a new billing period</Button>
        </SettingsRow>
      </SettingsSection>
      {creating && (
        <NewBillingPeriodDialog
          organisationId={organisationId}
          plans={plans}
          initialPlan={plan}
          currentPlanId={current?.id}
          onOpenChange={setCreating}
        />
      )}
    </>
  );
}
